import { useParams, Link } from 'react-router-dom'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { Helmet } from 'react-helmet-async'
import { Breadcrumb } from '@/components/Breadcrumb'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle } from 'lucide-react'
import bestCustomerSupportSoftware from '@/data/compareArticles/best-customer-support-software.json'
import { toAbsoluteUrl } from '@/lib/seo'

type CompareArticle = {
  slug: string
  metaTitle: string
  metaDescription: string
  keyTakeaways: string[]
  overview: string
  featureComparison: {
    intro: string
    features: Array<{
      name: string
      description: string
      feedbask: boolean
      intercom: boolean
      zendesk: boolean
      crisp: boolean
      gleap: boolean
      livagent: boolean
    }>
    tools: string[]
  }
  tools: Array<{
    rank: number
    name: string
    tagline: string
    description: string
    quote: string
    quoteAuthor: string
    quoteRole: string
    pros: string[]
    cons: string[]
    ctaText: string | null
    image?: string
  }>
  conclusion: string
  faq: Array<{ question: string; answer: string }>
  author: { name: string; role: string; bio: string }
}

const compareArticles: Record<string, CompareArticle> = {
  'best-customer-support-software': bestCustomerSupportSoftware as CompareArticle,
}

const CompareArticlePage = () => {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? compareArticles[slug] ?? null : null

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center p-10">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Article not found</h1>
          <Link to="/compare" className="text-[#866BFF] hover:underline">Back to Compare</Link>
        </div>
      </div>
    )
  }

  const year = new Date().getFullYear()
  const title = article.metaTitle.replace(/\d{4}/, String(year))
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Compare', url: '/compare' },
    { name: article.metaTitle.split('|')[0].trim(), url: `/compare/${article.slug}` },
  ]

  const breadcrumbSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: toAbsoluteUrl(crumb.url),
    })),
  }

  const faqSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }

  const articleSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: article.metaDescription,
    author: { '@type': 'Organization', name: article.author.name },
    publisher: {
      '@type': 'Organization',
      name: 'Feedbask',
      logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/logo_feedbask.png') },
    },
  }

  const toolKeys = ['feedbask', 'intercom', 'zendesk', 'crisp', 'gleap', 'livagent'] as const

  return (
    <div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={article.metaDescription} />
        <link rel="canonical" href={toAbsoluteUrl(`/compare/${article.slug}`)} />
        <link rel="alternate" hrefLang="en" href={toAbsoluteUrl(`/compare/${article.slug}`)} />
        <link rel="alternate" hrefLang="x-default" href={toAbsoluteUrl(`/compare/${article.slug}`)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={toAbsoluteUrl(`/compare/${article.slug}`)} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={article.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchemaData)}</script>
      </Helmet>

      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
        <div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl" />
        <div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        <Breadcrumb items={breadcrumbs} />

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="py-8 pb-20"
        >
          <header className="mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {article.metaTitle.split('|')[0].trim().replace(/\d{4}/, String(year))}
            </h1>
           
          </header>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Takeaways</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              {article.keyTakeaways.map((takeaway, i) => (
                <li key={i}>{takeaway}</li>
              ))}
            </ol>
          </section>

          <section className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed">{article.overview}</p>
          </section>

          <section className="mb-14" id="feature-comparison">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Feature Comparison</h2>
            <p className="text-gray-600 mb-6">{article.featureComparison.intro}</p>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full border-collapse min-w-[800px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Feature</th>
                    {article.featureComparison.tools.map((t) => (
                      <th key={t} className="py-3 px-2 text-center text-gray-900 font-semibold text-sm">
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {article.featureComparison.features.map((f, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{f.name}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{f.description}</p>
                      </td>
                      {toolKeys.map((key) => (
                        <td key={key} className="py-3 px-2 text-center">
                          {f[key] ? (
                            <CheckCircle className="inline w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="inline w-5 h-5 text-gray-300" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {article.tools.map((tool) => (
            <section key={tool.name} className="mb-14" id={tool.name.toLowerCase().replace(/\s+/g, '-')}>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {tool.rank}. {tool.name}
              </h2>
              <p className="text-[#866BFF] font-medium mb-4">({tool.tagline})</p>
              {tool.image && (
                <div className="mb-6 rounded-xl border border-gray-200 overflow-hidden shadow-sm w-1/2 max-w-2xl mx-auto">
                  <img
                    src={tool.image}
                    alt={`${tool.name} dashboard or interface`}
                    className="w-full h-auto object-contain"
                  />
                </div>
              )}
              <p className="text-gray-700 mb-4">{tool.description}</p>
              <blockquote className="border-l-4 border-[#866BFF] pl-4 py-2 my-4 text-gray-700 italic">
                "{tool.quote}"
                <footer className="mt-2 not-italic text-sm text-gray-600">
                  {tool.quoteAuthor}, {tool.quoteRole}
                </footer>
              </blockquote>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-5">
                  <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Pros
                  </h3>
                  <ul className="space-y-2">
                    {tool.pros.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-gray-500" />
                    Cons
                  </h3>
                  <ul className="space-y-2">
                    {tool.cons.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                        <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {tool.ctaText && (
                <div className="flex justify-center">
                  <Link
                    to="/auth"
                    className="inline-block bg-[#866BFF] text-white font-semibold px-6 py-2.5 rounded-full hover:bg-[#7556f0] transition animate-cta-subtle"
                  >
                    {tool.ctaText}
                  </Link>
                </div>
              )}
            </section>
          ))}

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Conclusion</h2>
            <p className="text-gray-700 leading-relaxed">{article.conclusion}</p>
          </section>

          <section className="mb-14" id="faq">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {article.faq.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900">{item.question}</h3>
                  <p className="mt-2 text-gray-700">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
              <div>
                <p className="font-semibold text-gray-900">{article.author.name}</p>
                <p className="text-sm text-gray-600">{article.author.role}</p>
                <p className="mt-2 text-gray-700 text-sm">{article.author.bio}</p>
              </div>
            </div>
          </section>

          <section className="bg-[#866BFF] text-white rounded-2xl p-10 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to collect better feedback?</h2>
            <p className="mb-6 max-w-xl mx-auto">
              Feedbask helps you collect user feedback, bug reports, feature requests, and reviews with one simple widget.
            </p>
            <Link
              to="/auth"
              className="inline-block bg-white text-[#866BFF] font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition"
            >
              Get started for free
            </Link>
          </section>
        </motion.article>

        <Footer />
      </div>
    </div>
  )
}

export default CompareArticlePage
